import { createApiClient, type components, type paths } from '@snaphire/api-client';
import { apiError, clearSession, ensureSessionFresh, refreshSession, token } from './session';
import { permissionGroupLabel } from '../i18n';
import { listQuery } from './list-query';

export type Schema<K extends keyof components['schemas']> = components['schemas'][K];
export type GetPath = {
  [P in keyof paths]: paths[P]['get'] extends undefined ? never : P;
}[keyof paths];
export type GetInit = {
  params?: { query?: Record<string, unknown>; path?: Record<string, string> };
  signal?: AbortSignal;
};
type Result = { data?: unknown; error?: unknown; response: Response };
type DynamicMethod = (
  path: keyof paths,
  init?: GetInit & { body?: unknown; headers?: Record<string, string> },
) => Promise<Result>;
export async function command(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: keyof paths,
  body?: unknown,
  params: Record<string, string> = {},
): Promise<Row> {
  const result = await (api[method] as unknown as DynamicMethod)(path, {
    params: { path: params },
    ...(body === undefined ? {} : { body }),
    headers: mutationHeaders(),
  });
  if (result.error || !result.response.ok) throw apiError(result.response, result.error);
  if (method === 'POST' && path === '/api/v1/accounts') {
    const created = result.data as Schema<'AccountCreatedResponse'>;
    return { ...created.account, actionLink: created.actionLink };
  }
  return (result.data || {}) as Row;
}
export const mutationHeaders = () => ({ 'Idempotency-Key': crypto.randomUUID() });
export type Row = { id: string; version?: number; [field: string]: unknown };
export type Page<T> = { items: T[]; page: { nextCursor: string | null; hasMore: boolean } };

export const api = createApiClient({
  credentials: 'include',
  fetch: async (request) => {
    await ensureSessionFresh();
    if (token()) request.headers.set('Authorization', `Bearer ${token()}`);
    const retry = request.clone();
    const response = await fetch(request);
    if (response.status !== 401 || !new URL(request.url).pathname.startsWith('/api/'))
      return response;
    try {
      await refreshSession();
      retry.headers.set('Authorization', `Bearer ${token()}`);
      return await fetch(retry);
    } catch {
      clearSession();
      return response;
    }
  },
});

export const read = api.GET as unknown as DynamicMethod;

api.use({
  onRequest({ request }) {
    if (token()) request.headers.set('Authorization', `Bearer ${token()}`);
    if (
      !['GET', 'HEAD', 'OPTIONS'].includes(request.method) &&
      !request.headers.has('Idempotency-Key')
    )
      request.headers.set('Idempotency-Key', crypto.randomUUID());
    return request;
  },
});

export async function getPage<T extends Row>(
  path: GetPath,
  query: Record<string, string | number | boolean | undefined> = {},
  signal?: AbortSignal,
  params: Record<string, string> = {},
): Promise<Page<T>> {
  const result = await read(path, {
    params: { query: listQuery(path, query), path: params },
    signal,
  });
  if (result.error || !result.data) throw apiError(result.response, result.error);
  return result.data as unknown as Page<T>;
}

export async function getObject<T>(
  path: GetPath,
  params: Record<string, string>,
  signal?: AbortSignal,
  query?: Record<string, unknown>,
): Promise<T> {
  const result = await read(path, { params: { path: params, query }, signal });
  if (result.error || !result.data) throw apiError(result.response, result.error);
  return result.data as unknown as T;
}

export function field(row: Row | undefined, key: string, fallback = ''): string {
  const value = row?.[key];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback;
}
export function name(row: Row | undefined): string {
  return (
    field(row, 'workName') ||
    (Array.isArray(row?.grants) ? permissionGroupLabel(field(row, 'name')) : field(row, 'name')) ||
    field(row, 'title') ||
    field(row, 'displayName') ||
    field(row, 'login')
  );
}
